import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { CropSettingsState, StateCropSetting } from '../../cropsetting.model';
import * as CropSettingActions from '../../store/actions/cropsetting.actions';
import { getCropSettings } from '../../store/selectors/cropsetting.selectors';

@Component({
  selector: 'app-cropsetting',
  templateUrl: './cropsetting.component.html',
  styleUrls: ['./cropsetting.component.scss'],
  standalone: false,
})
export class CropSettingsComponent implements OnInit {
  private readonly store = inject<Store<CropSettingsState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly locale = inject(LocaleService);

  paginator = viewChild(MatPaginator);
  currentYear = new Date().getFullYear();
  availableCropYears = Array.from({ length: this.currentYear + 2 - 2020 }, (_, k) => k + 2020);
  filterNameControl = new FormControl();
  filterYearControl = new FormControl();

  private readonly filterName = toSignal(
    this.filterNameControl.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      map((value: string) => value || ''),
      startWith(''),
    ),
    { initialValue: '' },
  );
  private readonly filterYear = toSignal(
    this.filterYearControl.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      map((value: string) => Number(value) || (null as number)),
      startWith(null as number),
    ),
    { initialValue: null as number },
  );

  cropSettings = toSignal(this.store.pipe(select(getCropSettings), distinctUntilChanged()), {
    initialValue: [] as StateCropSetting[],
  });

  filterValue = computed(() => ({
    cropName: this.filterName(),
    calendarYear: this.filterYear(),
  }));

  filteredCropSettings = computed(() => {
    const settings = this.cropSettings();
    if (!settings.length) return settings;
    const value = this.filterValue();
    return settings.filter(({ calendarYear, cropName }) => {
      const hasNoCropNameFilter = !value.cropName;
      const hasNoCalendarYearFilter = !value.calendarYear;
      const hasNoFilters = hasNoCropNameFilter && hasNoCalendarYearFilter;
      const hasCropNameMatch = cropName.toLowerCase().indexOf(value.cropName.toLowerCase()) !== -1;
      const hasCalendarYearMatch = calendarYear === value.calendarYear;
      return (
        hasNoFilters ||
        (hasNoCalendarYearFilter && hasCropNameMatch) ||
        (hasNoCropNameFilter && hasCalendarYearMatch) ||
        (hasCropNameMatch && hasCalendarYearMatch)
      );
    });
  });

  filterEnabled = computed(() => !!this.filterValue());

  pageSize = 10;
  page = signal<PageEvent>({
    pageIndex: 0,
    pageSize: this.pageSize,
    length: null,
  });

  pagedCropSettings = computed(() => {
    const cropSettings = this.filteredCropSettings();
    const { pageIndex, pageSize } = this.page();
    return cropSettings.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
  });

  isMobile = toSignal(this.breakpoint.ltLg$, { initialValue: false });
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'id',
        label: this.locale.translate(`id`),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'cropName',
        label: this.locale.translate(`cropName`),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'calendarYear',
        label: this.locale.translate(`year`),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'cultivationMonthsFrom',
        label: this.locale.translate(`cultivationMonthsFrom`),
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'cultivationMonthsUntil',
        label: this.locale.translate(`cultivationMonthsUntil`),
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'norm',
        label: this.locale.translate(`planetProofNormInKg`),
        type: TableColumnTypes.NUMBER,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('editCropSetting'),
        icon: 'edit',
        callback: (event: StateCropSetting) => this.editCropSetting(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('deleteCropSetting'),
        icon: 'delete',
        callback: (event: StateCropSetting) => this.deleteCropSetting(event),
        disabled: () => false,
      },
    ],
  };
  constructor() {
    effect(() => {
      this.filterValue();
      untracked(() => this.resetPagination());
    });

    effect(() => {
      this.tableConfig.dataSource.data = this.pagedCropSettings();
    });
  }

  ngOnInit() {
    this.store.dispatch(CropSettingActions.loadCropSettingsRequest());
  }

  addCropSetting() {
    this.store.dispatch(CropSettingActions.addCropSetting());
  }

  editCropSetting(cropSetting: StateCropSetting) {
    this.store.dispatch(CropSettingActions.editCropSetting({ cropSetting }));
  }

  deleteCropSetting(cropSetting: StateCropSetting) {
    this.store.dispatch(CropSettingActions.deleteCropSetting({ cropSetting }));
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  resetFilter() {
    this.filterNameControl.reset();
    this.filterYearControl.reset();
  }

  onPage(event: PageEvent) {
    this.page.set(event);
  }

  private resetPagination() {
    const p = this.paginator();
    if (p) {
      p.pageIndex = 0;
    }
    this.page.set({
      pageIndex: 0,
      pageSize: this.pageSize,
      length: null,
    });
  }
}
